import { useMutation, useQueryClient } from "react-query";

import useApiHelper from "../../services/useApiHelper";
import { nurtureCampaignQueryKeys } from "./queryKeys";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import {
  NurtureCampaignCreate,
  NurtureCampaignUpdate,
} from "../../models/nurtureCampaign/NurtureCampaign";
import {
  NurtureCampaignRuleCreate,
  NurtureCampaignRuleUpdate,
  SendTestEmail,
} from "../../models/nurtureCampaign/NurtureCampaignRule";

export function useNurtureCampaignMutations(agencyId: string) {
  const queryClient = useQueryClient();
  const { post, put, del } = useApiHelper();
  const { saveHandlers, deleteHandlers } =
    useDefaultCRUDHandlers("Nurture Campaign");

  const createCampaign = useMutation(
    (nurtureCampaign: NurtureCampaignCreate) =>
      post(`/marketing/${agencyId}/nurture`, nurtureCampaign),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(nurtureCampaignQueryKeys._base());
      },
    }
  );

  const updateCampaign = useMutation(
    (nurtureCampaign: NurtureCampaignUpdate) =>
      put(
        `/marketing/${agencyId}/nurture/${nurtureCampaign.id}`,
        nurtureCampaign
      ),
    {
      ...saveHandlers,
      onSuccess: () =>
        queryClient.invalidateQueries(nurtureCampaignQueryKeys._base()),
    }
  );

  const deleteCampaign = useMutation(
    (campaignId: string) => del(`/marketing/${agencyId}/nurture/${campaignId}`),
    {
      ...deleteHandlers,
      onSuccess: () => {
        queryClient.invalidateQueries(nurtureCampaignQueryKeys._base());
        deleteHandlers.onSuccess?.();
      },
    }
  );

  const cloneCampaign = useMutation(
    (campaignId: string) => put(`/marketing/${agencyId}/nurture/${campaignId}/clone`),
    {
      ...saveHandlers,
      onSuccess: () => {
        queryClient.invalidateQueries(nurtureCampaignQueryKeys._base());
        saveHandlers.onSuccess?.()
      },
    }
  );

  const createRule = useMutation(
    (rule: NurtureCampaignRuleCreate) =>
      post(
        `/marketing/${agencyId}/nurture/${rule.nurtureCampaignId}/rules`,
        rule
      ),
    {
      onSuccess: (_, vars) => {
        queryClient.invalidateQueries(
          nurtureCampaignQueryKeys.single(agencyId, vars.nurtureCampaignId)
        );
      },
    }
  );

  const updateRule = useMutation((rule: NurtureCampaignRuleUpdate) =>
    put(
      `/marketing/${agencyId}/nurture/${rule.nurtureCampaignId}/rules/${rule.id}`,
      rule
    )
  );

  const sendTest = useMutation((req: SendTestEmail) =>
    post(
      `/marketing/${agencyId}/nurture/${req.nurtureCampaignId}/rules/${req.ruleId}/send-test`,
      { email: req.email }
    )
  );

  return {
    createCampaign,
    updateCampaign,
    deleteCampaign,
    cloneCampaign,
    createRule,
    updateRule,
    sendTest,
  };
}
